import React, { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { IOrderDto } from "../../../types/order/IOrderDto"
import { CommonInput } from "../../common/element/fields/CommonInput"
import { IErrorModel } from "../../../types/error/IErrorModel"
import { CommonFileInput } from "../../common/element/fields/CommonFileInput"
import { CurrencyPicker } from "../../common/element/fields/CurrencyPicker"
import { MenuClient } from "../MenuClient"
import { Header } from "../../common/header/Header"
import { useNavigate, useParams } from "react-router-dom"
import { IClientContractorDto } from "../../../types/contractor/IClientContractorDto"
import { IClientContractorAccountDto } from "../../../types/contractor/IClientContractorAccountDto"
import { CountryPicker } from "../../common/element/fields/CountryPicker"
import { SwiftPicker } from "../../common/element/fields/SwiftPicker"
import { ICountry } from "../../../types/dict/ICountry"
import { ISwift } from "../../../types/dict/ISwift"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { IReplenishmentListProjection } from "../../../types/replenishment/IReplenishmentListProjection"
import { IReplenishmentDto } from "../../../types/replenishment/IReplenishmentDto"
import { round2Digits, spacedNum } from "../../common/CommonHelper"
import { CommonFileList } from "../../common/element/fields/CommonFileList"
import { IClientBalanceDto } from "../../../types/client/IClientBalanceDto"
import { IFullCompanyDto } from "../../../types/company/IFullCompanyDto"
import Button from "@/ui/components/Button"
import { ORDER_ROUTE } from "@/routes"

export const OrderClient = () => {
  const navigate = useNavigate()

  const [message, setMessage] = useState<IModalMessage | null>(null)
  const [order, setOrder] = useState<IOrderDto>()
  const [errors, setErrors] = useState<IErrorModel>()
  const [sending, setSending] = useState(false)
  const [invoiceAmount, setInvoiceAmount] = useState<string | undefined>(
    order?.invoiceAmount ? (Number(order.invoiceAmount) / 100).toString() : undefined
  )

  const [replenishments, setReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [currencyReplenishments, setCurrencyReplenishments] = useState<IReplenishmentListProjection[]>([])

  const [company, setCompany] = useState<IFullCompanyDto>()
  const [contractors, setContractors] = useState<IClientContractorDto[]>([])
  const [balance, setBalance] = useState<IClientBalanceDto>()
  const [filteredContractors, setFilteredContractors] = useState<IClientContractorDto[]>([])
  const [pickedContractor, setPickedContractor] = useState<IClientContractorDto>()
  const [pickedContractorAccounts, setPickedContractorAccounts] = useState<IClientContractorAccountDto[]>([])
  const [pickedContractorAccount, setPickedContractorAccount] = useState<IClientContractorAccountDto>()
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState<string | undefined>()

  const params = useParams()

  useEffect(() => {
    getContractors()
    getReplenishments()
    getCurrencyReplenishments()
    getBalance()
    if (params.id !== "new") {
      TrinityAPI.getOrder(Number(params.id), response => {
        if (response.status === 200) {
          setOrder(response.data)
          setInvoiceAmount((Number(response.data.invoiceAmount) / 100).toString())
        } else {
          setMessage({ title: "Ошибка", text: `Ошибка получения ордера: ${response.data.message}` })
        }
      })
    }
  }, [])

  useEffect(() => {
    setOrder({
      ...order,
      invoiceAmount: invoiceAmount ? (Number(invoiceAmount.toString().replaceAll(" ", "")) * 100).toString() : undefined
    })
  }, [invoiceAmount])

  const getContractors = () => {
    TrinityAPI.getClientContractors(response => {
      if (response.status === 200) {
        setContractors(response.data)
      }
    })
  }

  const getReplenishments = () => {
    TrinityAPI.getReplenishmentList("DONE", response => {
      if (response.status === 200) {
        setReplenishments(response.data)
      }
    })
  }

  const getCurrencyReplenishments = () => {
    TrinityAPI.getCurrencyReplenishmentList("DONE", response => {
      if (response.status === 200) {
        setCurrencyReplenishments(response.data)
      }
    })
  }

  const getBalance = () => {
    TrinityAPI.getBalance(response => {
      if (response.status === 200) {
        setBalance(response.data)
      }
    })
  }

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setFilteredContractors([])
      return
    }
    if (searchQuery?.length >= 3) {
      setFilteredContractors(
        contractors.filter(
          c =>
            c.name?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
            (c.country &&
              c.country.name &&
              c.country.name.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())) ||
            (c.accounts &&
              c.accounts.length > 0 &&
              c.accounts.filter(a => a.swift?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())).length >
                0) ||
            (c.accounts &&
              c.accounts.length > 0 &&
              c.accounts.filter(a => a.currency?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())).length >
                0)
        )
      )
    }
  }, [searchQuery])

  useEffect(() => {
    if (pickedContractor && pickedContractor.id) {
      TrinityAPI.getClientContractor(pickedContractor.id, response => {
        if (response.status === 200) {
          setPickedContractorAccounts(response.data.accounts || new Array([]))
          setOrder({
            ...order,
            contractorName: pickedContractor.name,
            contractorCountry: pickedContractor.country,
            contractorAddress: pickedContractor.address,
            contractorContactName: pickedContractor.contactName,
            contractorContactPhone: pickedContractor.contactPhone,
            contractorContactEmail: pickedContractor.contactEmail,
            contractorContactJobTitle: pickedContractor.contactJobTitle
          })
        }
      })
    }
  }, [pickedContractor])

  useEffect(() => {
    if (pickedContractorAccount) {
      setOrder({
        ...order,
        contractorSwift: pickedContractorAccount.swift,
        contractorCurrency: pickedContractorAccount.currency,
        contractorAccountNumber: pickedContractorAccount.accountNumber,
        contractorBankName: pickedContractorAccount.bankName
      })
    }
  }, [pickedContractorAccount])

  const pickReplenishment = (id: string) => {
    if (id === "") {
      setOrder({ ...order, replenishmentId: undefined, companyId: undefined })
      return
    }
    TrinityAPI.getReplenishment(Number(id), response => {
      if (response.status === 200) {
        const r: IReplenishmentDto = response.data
        setOrder({
          ...order,
          replenishmentId: r.id,
          currencyReplenishmentId: "",
          contractorCountry: r.contractorCountry,
          contractorAddress: r.contractorAddress,
          contractorName: r.contractorName,
          contractorBankName: r.contractorBankName,
          contractorCurrency: r.contractorCurrency,
          contractorSwift: r.contractorSwift,
          contractorContactName: r.contractorContactName,
          contractorAccountNumber: r.contractorAccountNumber,
          contractorContactEmail: r.contractorContactEmail,
          contractorContactPhone: r.contractorContactPhone,
          contractorContactJobTitle: r.contractorContactJobTitle,
          companyId: r.companyId
        })
      }
    })
  }

  const pickCurrencyReplenishment = (id: string) => {
    if (id === "") {
      setOrder({ ...order, currencyReplenishmentId: undefined, companyId: undefined })
      return
    }
    TrinityAPI.getCurrencyReplenishment(Number(id), response => {
      if (response.status === 200) {
        const r: IReplenishmentDto = response.data
        setOrder({
          ...order,
          currencyReplenishmentId: r.id,
          replenishmentId: "",
          contractorCountry: r.contractorCountry,
          contractorAddress: r.contractorAddress,
          contractorName: r.contractorName,
          contractorBankName: r.contractorBankName,
          contractorCurrency: r.contractorCurrency,
          contractorSwift: r.contractorSwift,
          contractorContactName: r.contractorContactName,
          contractorAccountNumber: r.contractorAccountNumber,
          contractorContactEmail: r.contractorContactEmail,
          contractorContactPhone: r.contractorContactPhone,
          contractorContactJobTitle: r.contractorContactJobTitle,
          companyId: r.companyId
        })
      }
    })
  }

  useEffect(() => {
    if (order?.companyId) {
      TrinityAPI.getCompany(order.companyId, response => {
        if (response.status === 200) {
          setCompany(response.data)
        } else {
          setCompany(undefined)
        }
      })
    } else {
      setCompany(undefined)
    }
  }, [order?.companyId])

  const pickContractor = (contractor: IClientContractorDto) => {
    setFilteredContractors([])
    setPickedContractor(contractor)
  }

  const pickAccount = (account: IClientContractorAccountDto, index: number) => {
    setCurrentAccountIndex(index)
    setPickedContractorAccount(account)
  }

  const changeCountry = (countryList: ICountry[]) => {
    if (countryList.length === 0) {
      setOrder({ ...order, contractorCountry: undefined })
    } else {
      setOrder({ ...order, contractorCountry: countryList[0] })
    }
  }

  const changeSwift = (swift: ISwift | undefined) => {
    if (swift) {
      setOrder({
        ...order,
        contractorSwift: swift.swift_code,
        contractorBankName: swift.bank_name
      })
    }
  }

  const submit = () => {
    if (sending || !order) return
    setSending(true)

    const payload = {
      ...order,
      invoiceAmount: Math.round(Number(order.invoiceAmount)).toString()
    }

    TrinityAPI.submitOrder(payload, response => {
      if (response.status === 200) {
        window.location.href = "/order"
      } else {
        setSending(false)
        setErrors(response.data)
        //setMessage({title: "Ошибка!", text: `Ошибка при создании ордера: ${response.data.message}`})
      }
    })
  }

  return (
    <>
      <>
        <ModalPopUp message={message} close={() => setMessage(null)} />
        <MenuClient active={"order"} />
        <main className="main-container" id="scrollable">
          <Header />
          <div className="container">
            <h3 className="ui-heading">{order?.id ? `Ордер №${order.id}` : "Новый ордер"}</h3>
            {order?.status === "NEW" ? (
              <p className="message-course-yes">
                Ордер успешно создан и будет исполнен в ближайшее время. После исполнения вы сможете скачать slip и
                агентский инвойс, а также увидеть изменения на балансе счета.
              </p>
            ) : (
              <></>
            )}
            <div
              className="container-pop-up"
              style={order?.id ? { pointerEvents: "none", opacity: 0.7 } : {}}
              aria-disabled={true}
            >
              <div className="form-block-popup width-full">
                <h4 className="heading-chapter">Выбор пополнения</h4>
                {order?.id ? (
                  <></>
                ) : (
                  <p className="message-course-yes">
                    Вы можете создать ордер на основании пополнения, тогда все поля будут заполнены автоматически
                    (данные будут взяты из пополнения, которое вы совершали ранее). В противном случае вы можете не
                    заполнять это поле, однако баланс счета должен быть достаточным, для создания ордера
                  </p>
                )}
                <fieldset>
                  <legend>На основании рублевого пополнения</legend>
                  <select
                    className="input-form"
                    onChange={e => pickReplenishment(e.target.value)}
                    value={order?.replenishmentId}
                  >
                    <option value="">Выберите пополнение</option>
                    {replenishments.map((r, index) => (
                      <option value={r.id} key={index}>
                        {r.id} ({r.contractor}: {spacedNum(round2Digits(Number(r.amount) / 100))} {r.currency})
                      </option>
                    ))}
                  </select>
                </fieldset>
                <fieldset>
                  <legend>На основании валютного пополнения</legend>
                  <select
                    className="input-form"
                    onChange={e => pickCurrencyReplenishment(e.target.value)}
                    value={order?.currencyReplenishmentId}
                  >
                    <option value="">Выберите пополнение</option>
                    {currencyReplenishments.map((r, index) => (
                      <option value={r.id} key={index}>
                        {r.id} ({r.currencyPayer}: {spacedNum(round2Digits(Number(r.amount) / 100))} {r.currency})
                      </option>
                    ))}
                  </select>
                </fieldset>
              </div>

              {order && order.status ? (
                <></>
              ) : (
                <div className="form-block-popup width-full">
                  <h4 className="heading-chapter">Поиск контрагента</h4>
                  <fieldset>
                    <input
                      type="search"
                      className="input-form-search width-full"
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Поиск по названию, стране, SWIFT или валюте"
                    />
                    <ul className="prompt-list">
                      {filteredContractors?.map((c: IClientContractorDto, index: number) => (
                        <li className="promp-item" key={index} onClick={() => pickContractor(c)}>
                          {c.name}
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                  {pickedContractor && pickedContractorAccounts.length > 0 ? (
                    <>
                      <li className="form-block-popup">
                        <h4 className="heading-chapter">Выберите счет</h4>
                        <div className="flex-row-10">
                          {pickedContractorAccounts.map((account: IClientContractorDto, index: number) => (
                            <button
                              className={`btn ${
                                pickedContractorAccount && pickedContractorAccount.id === account.id
                                  ? "black-btn"
                                  : "grey-btn"
                              } btn-bank-account`}
                              key={index}
                              onClick={() => pickAccount(account, index)}
                            >
                              Счет {index + 1}
                            </button>
                          ))}
                        </div>
                        {pickedContractorAccounts.map((a, index) => (
                          <React.Fragment key={index}>
                            <div
                              className="flex-row-10"
                              style={{ display: index === currentAccountIndex ? "" : "none" }}
                            >
                              <fieldset>
                                <legend>SWIFT</legend>
                                <p className="text-form">{a.swift}</p>
                              </fieldset>
                              <fieldset>
                                <legend>IBAN</legend>
                                <p className="text-form">{a.accountNumber}</p>
                              </fieldset>
                            </div>
                            <div
                              className="flex-row-10"
                              style={{ display: index === currentAccountIndex ? "" : "none" }}
                            >
                              <fieldset style={{ display: index === currentAccountIndex ? "" : "none" }}>
                                <legend>Банк</legend>
                                <p className="text-form">{a.bankName}</p>
                              </fieldset>
                              <fieldset className="fieldset-200">
                                <legend>Валюта</legend>
                                <p className="text-form">{a.currency}</p>
                              </fieldset>
                            </div>
                          </React.Fragment>
                        ))}
                      </li>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              )}

              <div className="container-pop-up">
                <ul className="form-left">
                  <li className="form-block-popup">
                    <h4 className="heading-chapter">Получатель валютных средств</h4>
                    <CommonInput
                      typeName={"text"}
                      label={"Полное название"}
                      placeholder={"Название компании"}
                      name={"contractorName"}
                      value={order?.contractorName}
                      errorModel={errors}
                      _callback={e =>
                        setOrder({
                          ...order,
                          contractorName: e
                        })
                      }
                    />
                    <CountryPicker
                      label={"Страна"}
                      value={order?.contractorCountry ? new Array(order?.contractorCountry) : []}
                      size={1}
                      name={"contractorCountry"}
                      errorModel={errors}
                      _callback={changeCountry}
                    />
                    <CommonInput
                      typeName={"text"}
                      label={"Адрес"}
                      placeholder={"Адрес компании"}
                      name={"contractorAddress"}
                      value={order?.contractorAddress}
                      errorModel={errors}
                      _callback={e =>
                        setOrder({
                          ...order,
                          contractorAddress: e
                        })
                      }
                    />
                  </li>

                  <li className="form-block-popup">
                    <h4 className="heading-chapter">Счет</h4>
                    <div className="flex-row-10">
                      <SwiftPicker
                        label={"SWIFT"}
                        name={"contractorSwift"}
                        value={order?.contractorSwift}
                        errorModel={errors}
                        _callback={changeSwift}
                      />
                      <CommonInput
                        typeName={"text"}
                        label={"IBAN"}
                        placeholder={"Введите номер счета"}
                        name={`contractorAccountNumber`}
                        value={order?.contractorAccountNumber}
                        errorModel={errors}
                        _callback={e =>
                          setOrder({
                            ...order,
                            contractorAccountNumber: e
                          })
                        }
                      />
                    </div>
                    <div className="flex-row-10">
                      <CommonInput
                        typeName={"text"}
                        label={"Банк"}
                        placeholder={"Название банка"}
                        name={`contractorBankName`}
                        value={order?.contractorBankName}
                        errorModel={errors}
                        _callback={e =>
                          setOrder({
                            ...order,
                            contractorBankName: e
                          })
                        }
                      />
                      <CurrencyPicker
                        label={"Валюта счета"}
                        name={`contractorCurrency`}
                        errorModel={errors}
                        initValue={order?.contractorCurrency}
                        _callback={e =>
                          setOrder({
                            ...order,
                            contractorCurrency: e
                          })
                        }
                        clazzName={"fieldset-200"}
                      />
                    </div>
                  </li>
                </ul>

                <div className="right-form">
                  <div className="form-block-popup">
                    <h4 className="heading-chapter">Контакты</h4>
                    <CommonInput
                      typeName={"text"}
                      label={"ФИО"}
                      placeholder={"ФИО"}
                      name={"contractorContactName"}
                      value={order?.contractorContactName}
                      errorModel={errors}
                      _callback={e =>
                        setOrder({
                          ...order,
                          contractorContactName: e
                        })
                      }
                    />
                    {/*<CommonInput*/}
                    {/*    typeName={"text"}*/}
                    {/*    label={"Должность"}*/}
                    {/*    placeholder={"Должность"}*/}
                    {/*    name={"contractorContactJobTitle"}*/}
                    {/*    value={order?.contractorContactJobTitle}*/}
                    {/*    errorModel={errors}*/}
                    {/*    _callback={(e) => setOrder({*/}
                    {/*      ...order,*/}
                    {/*      contractorContactJobTitle: e*/}
                    {/*    })}/>*/}
                    <div className="flex-row-10">
                      {/*<CommonInput*/}
                      {/*    typeName={"text"}*/}
                      {/*    label={"Email"}*/}
                      {/*    placeholder={"mail@mail.com"}*/}
                      {/*    name={"contractorContactEmail"}*/}
                      {/*    value={order?.contractorContactEmail}*/}
                      {/*    errorModel={errors}*/}
                      {/*    _callback={(e) => setOrder({*/}
                      {/*      ...order,*/}
                      {/*      contractorContactEmail: e*/}
                      {/*    })}/>*/}
                      <CommonInput
                        typeName={"text"}
                        label={"Телефон"}
                        placeholder={"+7 200 000 00 00"}
                        name={"contractorContactPhone"}
                        value={order?.contractorContactPhone}
                        errorModel={errors}
                        _callback={e =>
                          setOrder({
                            ...order,
                            contractorContactPhone: e
                          })
                        }
                      />
                    </div>
                  </div>

                  {order && order.status && order.status !== "NEW" ? (
                    <div className="form-block-popup">
                      <h4 className="heading-chapter">Расчет суммы</h4>
                      <div className="flex-column-5">
                        <p className="money-header">Ордер к исполнению</p>
                        <p className="money-summ-minus">
                          -{spacedNum(order.amountUsd ? Number(order.amountUsd) / 100 : "-")} USD
                        </p>
                      </div>
                      <div className="flex-column-5">
                        <p className="money-header">По курсу USD/{order.invoiceCurrency}</p>
                        <p className="money-summ">{order.xRate}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="form-block-popup">
                      <h4 className="heading-chapter">Баланс счета</h4>
                      <p className="balance-num">
                        {balance?.amount ? (
                          <>
                            {spacedNum(balance.amount / 100)}
                            <span className="valuta">USD</span>
                          </>
                        ) : (
                          <>
                            0 <span className="valuta">USD</span>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  {company ? (
                    <div className="form-block-popup">
                      <h4 className="heading-chapter">Компания плательщик</h4>
                      <div className="flex-column-5">
                        <p className="money-summ">{company.name}</p>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              <ul className="form-left">
                <li className="form-block-popup">
                  <h4 className="heading-chapter">Инвойс</h4>
                  <div className="flex-row-10 flex-wrap-mobile">
                    <CommonInput
                      typeName={"date"}
                      label={"Дата"}
                      placeholder={""}
                      name={"invoiceDate"}
                      value={order?.invoiceDate}
                      errorModel={errors}
                      _callback={e => setOrder({ ...order, invoiceDate: e })}
                    />
                    <CommonInput
                      typeName={"text"}
                      label={"Номер"}
                      placeholder={"Введите номер инвойса"}
                      name={"invoiceNumber"}
                      value={order?.invoiceNumber}
                      errorModel={errors}
                      _callback={e => setOrder({ ...order, invoiceNumber: e })}
                    />
                    <CommonFileInput
                      label={"Инвойс (файл)"}
                      placeholder={order?.invoice?.b64 || order?.invoicePath ? "Заменить" : "Загрузить"}
                      name={"invoice"}
                      errorModel={errors}
                      _callback={f => setOrder({ ...order, invoice: f })}
                      clazzName="fieldset-fit"
                    />
                  </div>
                  <div className="flex-row-10">
                    <CommonInput
                      typeName={"number"}
                      label={"Сумма"}
                      placeholder={"Введите сумму"}
                      name={"invoiceAmount"}
                      value={invoiceAmount}
                      errorModel={errors}
                      _callback={e => setInvoiceAmount(e)}
                    />
                    <CurrencyPicker
                      label={"Валюта"}
                      name={"invoiceCurrency"}
                      errorModel={errors}
                      initValue={order?.invoiceCurrency}
                      _callback={e => setOrder({ ...order, invoiceCurrency: e })}
                      clazzName="fieldset-200"
                    />
                  </div>
                  <CommonFileList file={order?.invoice} />
                </li>
              </ul>
            </div>
            <div className="flex-row-10 flex-wrap">
              {order?.id ? null : (
                <Button onClick={submit} loading={sending}>
                  Создать
                </Button>
              )}
              <Button variant="grey" onClick={() => navigate(ORDER_ROUTE)}>
                {order?.id ? "Назад" : "Отменить"}
              </Button>
            </div>
          </div>
        </main>
      </>
    </>
  )
}
